import type { Lang } from '@/data/types';
import { copyOverrideFor } from '@/lib/portalCopy';
import { PIPELINE_DEPARTMENTS } from '@shared/roster';

/**
 * The 4 portal stage groups for the tickets board. Every HubSpot pipeline
 * (per country/service) keeps its own real stages — these groups only decide
 * WHICH COLUMN a ticket appears in. The ticket detail keeps showing the real
 * HubSpot stage label.
 *
 * Mapping strategy (per product): be precise, pipeline by pipeline —
 *  1. EXACT table below, built from the real HubSpot stage labels
 *     (ES/FR/PT/IT immigration + nationality pipelines, fetched 2026-07)
 *     plus the mock roster stages.
 *  2. Pattern classifier for renamed/new stages, ordered so composite labels
 *     ("Gathering and Review of Documents") land on the right group.
 */
export type StageGroupId = 'docs' | 'review' | 'final' | 'waiting';

export const STAGE_GROUPS: { id: StageGroupId; label: Record<Lang, string> }[] = [
  // 1 · the pre-work phase: previous formalities + docs list sent (+ POA/NIF/bank for PT)
  { id: 'docs', label: { en: 'Initial formalities', es: 'Formalidades previas', fr: 'Formalités initiales', pt: 'Formalidades iniciais' } },
  // 2 · everything document-work: gathering, review, translation, extra doc requests
  { id: 'review', label: { en: 'Docs review', es: 'Docs review', fr: 'Revue des docs', pt: 'Revisão de docs' } },
  { id: 'final', label: { en: 'Final check', es: 'Chequeo final', fr: 'Contrôle final', pt: 'Verificação final' } },
  { id: 'waiting', label: { en: 'Waiting resolution', es: 'Esperando resolución', fr: 'Attente de résolution', pt: 'Aguardando resolução' } },
];

/**
 * THE SAME FOUR COLUMNS, IN EACH DEPARTMENT'S LANGUAGE.
 * Approved by the team (July 2026): the board keeps its shape everywhere —
 * what changes is what the columns are called, because "Docs review" says
 * nothing to a tax accountant and "Preparación fiscal" says everything.
 * Departments without their own row (Immigration, Corporate…) keep the
 * base labels above.
 */
const DEPT_GROUP_LABELS: Record<string, Record<StageGroupId, Record<Lang, string>>> = {
  /* Corporate (24 Sept 2026), named after its REAL HubSpot stages (Company
     Formation, Domiciliation, NIF & Foreign Employer, Corporate Secretary):
     until now it borrowed Spain's Immigration names and nearly every
     Corporate stage fell into column 1. Renamable from the admin panel. */
  Corporate: {
    docs:    { en: 'Onboarding & powers', es: 'Alta y poderes', fr: 'Ouverture et pouvoirs', pt: 'Abertura e procurações' },
    review:  { en: 'Incorporation', es: 'Constitución', fr: 'Constitution', pt: 'Constituição' },
    final:   { en: 'Signing & registry', es: 'Firma y registro', fr: 'Signature et registre', pt: 'Escritura e registo' },
    waiting: { en: 'Bank & start of activity', es: 'Banco e inicio de actividad', fr: 'Banque et début d’activité', pt: 'Banco e início de atividade' },
  },
  Tax: {
    docs:    { en: 'Onboarding & docs', es: 'Alta y documentación', fr: 'Ouverture et documents', pt: 'Abertura e documentos' },
    review:  { en: 'Tax preparation', es: 'Preparación fiscal', fr: 'Préparation fiscale', pt: 'Preparação fiscal' },
    final:   { en: 'Review & filing', es: 'Revisión y presentación', fr: 'Révision et dépôt', pt: 'Revisão e entrega' },
    waiting: { en: 'Awaiting authority', es: 'Esperando a la administración', fr: 'Attente de l’administration', pt: 'À espera da administração' },
  },
  'Real Estate': {
    docs:    { en: 'Engagement & due diligence', es: 'Encargo y due diligence', fr: 'Mandat et due diligence', pt: 'Mandato e due diligence' },
    review:  { en: 'Contracts & deposit', es: 'Contratos y arras', fr: 'Contrats et arrhes', pt: 'Contratos e sinal' },
    final:   { en: 'Signing preparation', es: 'Preparación de firma', fr: 'Préparation de signature', pt: 'Preparação de escritura' },
    waiting: { en: 'Signing & registry', es: 'Firma y registro', fr: 'Signature et registre', pt: 'Escritura e registo' },
  },
  Recurring: {
    docs:    { en: 'Service onboarding', es: 'Alta del servicio', fr: 'Mise en place du service', pt: 'Ativação do serviço' },
    review:  { en: 'Period work', es: 'Trabajo del período', fr: 'Travail de la période', pt: 'Trabalho do período' },
    final:   { en: 'Review & delivery', es: 'Revisión y entrega', fr: 'Révision et livraison', pt: 'Revisão e entrega' },
    waiting: { en: 'Up to date / renewal', es: 'Al día / Renovación', fr: 'À jour / Renouvellement', pt: 'Em dia / Renovação' },
  },
};

/* ═══ SPAIN (SP/MX Immigration pipeline) — its own four columns ═══
 *
 * Restructured with the team (Aug 2026) to mirror the REAL HubSpot stages:
 *   1 docs    · Gathering & review docs   ← Previous Formalities · Gathering and Review of Documents
 *   2 review  · Waiting / Appointment     ← Appointment at the Consulate · Submission · Re-Submission ·
 *                                            Waiting on Resolution · Additional Documentation Required
 *   3 final   · Processing / Post-approval← PA Formalities / RP Docs Gather · Fingerprints Appointment
 *   4 waiting · Card collection           ← Waiting for Card Collection
 *
 * The four internal ids stay the same everywhere (they are only positions);
 * what changes for Spain is which stages land on each and what the columns
 * are CALLED. France and the rest keep the shared mapping untouched.
 * Submission/Re-Submission were not in the team's list (both sat at 0) —
 * they wait on an authority, so they sit in column 2 with the waiting.
 * On Hold sits in column 2 as well: paused is a kind of waiting.
 */
const ES_LIKE = (country?: string) => country === 'ES' || country === 'MX';

const ES_EXACT: Record<string, StageGroupId> = {
  'previous formalities': 'docs',
  'gathering and review of documents': 'docs',
  'gathering & review of documents': 'docs',
  'appoitment at the consulate / submission': 'review', // (sic — HubSpot typo)
  'appointment at the consulate / submission': 'review',
  'submission': 'review',
  're-submission': 'review',
  're - submission': 'review', // as the HubSpot column literally spells it
  'waiting on resolution': 'review',
  'additional documentation requested': 'review',
  'additional documentation required': 'review',
  'on hold': 'review',
  'pa formalities / rp docs gather': 'final',
  'pa formalities / rp docs gathering': 'final',
  'fingerprints appointment': 'final',
  'waiting for card collection': 'waiting',
};

const ES_GROUP_LABELS: Record<StageGroupId, Record<Lang, string>> = {
  docs:    { en: 'Gathering & review docs', es: 'Recopilación y revisión de docs', fr: 'Collecte et revue des docs', pt: 'Recolha e revisão de docs' },
  review:  { en: 'Waiting reso. / Appointment', es: 'Esperando reso. / Cita', fr: 'Attente reso. / RDV', pt: 'A aguardar reso. / Marcação' },
  final:   { en: 'Processing / Post-approval', es: 'Procesando / Post-aprobación', fr: 'Traitement / Post-approbation', pt: 'Processamento / Pós-aprovação' },
  waiting: { en: 'Card collection', es: 'Recogida de tarjeta', fr: 'Retrait de la carte', pt: 'Recolha do cartão' },
};

/* ═══ PORTUGAL (PT Immigration · Corporate · Tax · Real Estate / Golden Visa) ═══
 *
 * Portugal asked for these four column names on 22 Sept 2026, after a board
 * review with the Lisbon team: they read a file by where it sits in the
 * AIMA / consulate circuit, and the shared "Docs review" column hid the one
 * difference that matters to them — whether we are still chasing papers or
 * the file has already gone in. The four internal ids (docs · review · final
 * · waiting) are ONLY positions 1 to 4; they keep their names in code so no
 * caller, override key or pinned stage has to change:
 *   1 docs    · Previous Formalities       ← onboarding, POA, NIF/NIPC, bank account
 *   2 review  · Gathering Documents        ← gathering, back and forward, certification, translation
 *   3 final   · Doc Review and Submission  ← our own review, file preparation, AIMA/consulate submission
 *   4 waiting · Waiting Resolution         ← the file is in, the next move is the authority's
 *
 * Two stages deliberately leave the shared table here: "Doc(s) Review" is
 * Portugal's column 3 and not its column 2, and "Submission" is column 3 too,
 * because in Portugal the submission appointment is still our own work — the
 * waiting starts once AIMA (or the consulate) holds the file. Spain, France,
 * Italy and the rest keep the shared mapping untouched.
 */
const PT_LIKE = (country?: string) => country === 'PT';

const PT_EXACT: Record<string, StageGroupId> = {
  // 1 · Previous Formalities — everything before a single client document is chased
  'previous formalities': 'docs',
  'parallel - client onboarding': 'docs',
  'parallel – client onboarding': 'docs', // HubSpot spells this one with an en dash
  'client onboarding': 'docs',
  'power of attorney': 'docs',
  'power of attorney/nif': 'docs',
  'power of attorney / nif': 'docs',
  'nif': 'docs',
  'nipc': 'docs',
  'nif/nipc': 'docs',
  'nif / nipc': 'docs',
  'bank account': 'docs',
  'intake': 'docs',
  'new case': 'docs',
  'list of documents sent': 'docs',
  // 2 · Gathering Documents — the papers are out with the client, the notary or a translator
  'gathering documents': 'review',
  'gathering of documents': 'review',
  'gathering and review of documents': 'review',
  'gathering & review of documents': 'review',
  'back and forward with the client': 'review', // as the pipeline literally spells it
  'back and forth with the client': 'review',
  'additional documentation requested': 'review',
  'additional documentation required': 'review',
  'certification of documents': 'review',
  'translation of documents': 'review',
  'translation': 'review',
  // 3 · Doc Review and Submission — the file is in our hands and goes in from here
  'doc review': 'final',
  'docs review': 'final',
  'review': 'final',
  'review documents': 'final',
  'review of documents': 'final',
  'file preparation': 'final',
  'file preparation & drafting': 'final',
  'file preparation & drafting of the application': 'final',
  'file preparation and drafting of the application': 'final',
  'submission': 'final',
  'submission of documents': 'final',
  're-submission': 'final',
  're - submission': 'final', // as the HubSpot column literally spells it
  'aima appointment': 'final',
  'appointment at aima': 'final',
  'appoitment at the consulate / submission': 'final', // (sic — HubSpot typo)
  'appointment at the consulate / submission': 'final',
  'consulado': 'final',
  'agendamento': 'final',
  'agendamento aima': 'final',
  // 4 · Waiting Resolution — nothing left for us to do but chase the authority
  'post submission': 'waiting',
  'post-submission': 'waiting',
  'waiting on resolution': 'waiting',
  'waiting resolution': 'waiting',
  'waiting for issuance': 'waiting',
  'follow up with aima': 'waiting',
  'follow-up with aima': 'waiting',
  'awaiting decision': 'waiting',
  'on hold': 'waiting',
};

const PT_GROUP_LABELS: Record<StageGroupId, Record<Lang, string>> = {
  docs:    { en: 'Previous Formalities', es: 'Formalidades previas', fr: 'Formalités préalables', pt: 'Formalidades prévias' },
  review:  { en: 'Gathering Documents', es: 'Recopilación de documentos', fr: 'Collecte des documents', pt: 'Recolha de documentos' },
  final:   { en: 'Doc Review and Submission', es: 'Revisión y presentación de docs', fr: 'Revue et dépôt des docs', pt: 'Revisão e submissão' },
  waiting: { en: 'Waiting Resolution', es: 'Esperando resolución', fr: 'Attente de résolution', pt: 'A aguardar resolução' },
};

/** Stable slug for a department inside a copy key ("Real Estate" → "real-estate"). */
export const stageDeptSlug = (dept: string): string =>
  dept.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-');

/** The copy key that renames one column FOR ONE COUNTRY. Kept apart from the
 *  department keys ("stage.docs.tax") by its own `country.` segment, so a
 *  jurisdiction and a department can never collide. */
export const stageCountryKey = (id: StageGroupId, country: string): string =>
  `stage.${id}.country.${country.trim().toLowerCase()}`;

/** The column's name as this department says it. Resolution order:
 *  the admin's rename FOR THIS department (live copy override) → the
 *  department's built-in vocabulary → the admin's base rename (Immigration's,
 *  which is the shared default) → the built-in base label. */
export function stageGroupLabel(id: StageGroupId, dept: string, lang: Lang, country?: string): string {
  if (dept) {
    const own = copyOverrideFor(`stage.${id}.${stageDeptSlug(dept)}`);
    if (own?.[lang]) return own[lang] as string;
  }
  /* What THIS jurisdiction calls the column, when an admin has renamed it —
     ahead of every built-in vocabulary, because a name somebody typed for
     their own country beats a default written in code. */
  if (country) {
    const own = copyOverrideFor(stageCountryKey(id, country));
    if (own?.[lang]) return own[lang] as string;
  }
  if (dept) {
    const deptName = DEPT_GROUP_LABELS[dept]?.[id]?.[lang];
    if (deptName) return deptName;
  }
  // Spain's own column names (see the ES block above). A department with its
  // own vocabulary (Tax, Real Estate…) already returned before this line.
  if (ES_LIKE(country)) return ES_GROUP_LABELS[id][lang];
  // Portugal's own column names (see the PT block above), read in the same
  // slot as Spain's: behind the admin's per-country rename and behind a
  // department's vocabulary, because both of those were typed by somebody on
  // purpose and a built-in jurisdiction name never gets to overrule them.
  if (PT_LIKE(country)) return PT_GROUP_LABELS[id][lang];
  const base = copyOverrideFor(`stage.${id}`);
  if (base?.[lang]) return base[lang] as string;
  return STAGE_GROUPS.find((g) => g.id === id)!.label[lang];
}

/** Normalize: lowercase, strip country prefixes ("IT - ", "PT - ") and spacing.
 *  The prefix goes whichever dash HubSpot typed it with. Portugal's pipeline
 *  spells "Parallel – Client Onboarding" with an en dash, which is why the
 *  PT table below carries that spelling as a key (22 Sept 2026); the same hand
 *  writing "PT – Submission" would have walked straight past PT_EXACT into the
 *  shared table, where Submission means waiting instead of column 3, and the
 *  board would have shown two Portuguese cases of the same stage in different
 *  columns. No stage label starts with a country code otherwise, so widening
 *  the dash can only add matches. */
const norm = (s: string) =>
  s.toLowerCase().replace(/^\s*(it|pt|fr|es|sp)\s*[-–—]\s*/i, '').replace(/\s+/g, ' ').trim();

/** Exact stage-label → group. Keys are normalized labels. */
const EXACT: Record<string, StageGroupId> = {
  // — initial formalities (pre-work: kickoff + docs list, POA/NIF/bank setup)
  'previous formalities': 'docs',
  'list of documents sent': 'docs',
  'power of attorney/nif': 'docs',
  'bank account': 'docs',
  'new case': 'docs',
  'intake': 'docs',
  // — docs review (all document work: gathering, review, translation, extras)
  'gathering and review of documents': 'review',
  'gathering & review of documents': 'review',
  'rp documents gathering': 'review',
  'additional documentation requested': 'review',
  'rp additional documentation requested': 'review',
  'documents': 'review',
  'civil acts': 'review',
  'docs review': 'review',
  'review': 'review',
  'translation of documents': 'review',
  'filiation chain': 'review',
  // — final verification: the file is being PREPARED and checked by us
  'final check': 'final',
  'file preparation & drafting of the application': 'final',
  'file preparation and drafting of the application': 'final',
  // The label as FR's Resident Permit pipeline actually spells it (seen on a
  // real ticket) — the board screenshot had it truncated to "Drafting of…".
  'file preparation & drafting of cover letter': 'final',
  'file preparation and drafting of cover letter': 'final',
  'file preparation & drafting': 'final',
  'file preparation': 'final',
  'appoitment at the consulate / submission': 'final', // (sic — HubSpot typo)
  'appointment at the consulate / submission': 'final',
  're-submission': 'final',
  'filing': 'final',
  // — submitted / waiting on an authority. Submission lives HERE, not in
  //   final: once the documents left our hands the work is the authority's,
  //   and the column has to say whose turn it is (asked for by the team,
  //   after File Preparation took over the final-check column).
  'submission': 'waiting',
  'submission of documents': 'waiting',
  'rp submission': 'waiting',
  'waiting on resolution': 'waiting',
  'waiting resolution': 'waiting',
  'waiting for nulla osta': 'waiting',
  'awaiting decision': 'waiting',
  'submitted': 'waiting',
  'filed': 'waiting',
  'greffe review': 'waiting',
  'fingerprints appointment': 'waiting',
  'fingerprinting appointment': 'waiting',
  'waiting for card collection': 'waiting',
  'registering at the city hall': 'waiting',
  'post approval formalities': 'waiting',
  'on hold': 'waiting',
};

/** The stages the portal knows out of the box (exact table) — shown in the
 *  admin stages manager so the team can see and re-pin what exists today. */
export const KNOWN_STAGES: { label: string; grp: StageGroupId }[] =
  Object.entries(EXACT).map(([label, grp]) => ({ label, grp }));

/** Admin-pinned stage→column overrides (Portal management panel). Injected at
 *  app start from the server, so brand-new HubSpot stages can be placed on the
 *  right column without a deploy. Highest priority. */
let OVERRIDES: Record<string, StageGroupId> = {};
export function setStageOverrides(list: { label: string; grp: string }[]) {
  const map: Record<string, StageGroupId> = {};
  for (const o of list) {
    const g = o.grp as StageGroupId;
    if (g === 'docs' || g === 'review' || g === 'final' || g === 'waiting') map[norm(o.label)] = g;
  }
  OVERRIDES = map;
}

/* CORPORATE'S STAGES, as HubSpot names them (read from the Spanish boards,
   24 Sept 2026). «Bank account» means the opposite in Immigration (an early
   formality) and in Corporate (the step after the deed), so this table is
   read only for a Corporate pipeline — see `pipeline` below. */
const CORP_EXACT: Record<string, StageGroupId> = {
  'introduction': 'docs',
  'power of attorney': 'docs',
  'nif': 'docs',
  'power of attorney/nif': 'docs',
  'incorporation pending': 'review',
  'incorporation deed preparation': 'review',
  'on hold': 'review',
  'incorporation deed signature': 'final',
  'commercial registry': 'final',
  'bank account': 'waiting',
  'start of activity and ss activation': 'waiting',
};

/* REAL ESTATE'S STAGES (pipeline «Real Estate», read from the boards, 24 Sept
   2026 — 24 tickets). They had their column NAMES since July but no stage
   table, so every one of them fell into column 1. Now, under those names:
   Engagement & due diligence · Contracts & deposit · Signing preparation ·
   Signing & registry. */
const RE_EXACT: Record<string, StageGroupId> = {
  'initial bureaucracy': 'docs',
  'due diligence': 'docs',
  'reservation contract': 'review',
  'private purchase agreement': 'review',
  'on hold': 'review',
  'final deed': 'final',
  'post completion': 'waiting',
};
const DEPT_EXACT: Record<string, Record<string, StageGroupId>> = { Corporate: CORP_EXACT, 'Real Estate': RE_EXACT };

/** Which column a stage belongs to. Admin overrides → the department's own
 *  table (Corporate, Real Estate — when the ticket's pipeline is theirs) → Spain's own table
 *  (when the ticket is Spain's/Mexico's) → Portugal's own table (when the
 *  ticket is Portugal's) → shared exact table → patterns. */
export function stageGroupOf(stageLabel: string, country?: string, pipeline?: string): StageGroupId {
  const s = norm(stageLabel || '');
  const pinned = OVERRIDES[s];
  if (pinned) return pinned;
  const deptTable = pipeline ? DEPT_EXACT[PIPELINE_DEPARTMENTS[pipeline] || ''] : undefined;
  if (deptTable) {
    const own = deptTable[s];
    if (own) return own;
  }
  if (ES_LIKE(country)) {
    const es = ES_EXACT[s];
    if (es) return es;
  }
  if (PT_LIKE(country)) {
    const pt = PT_EXACT[s];
    if (pt) return pt;
  }
  const exact = EXACT[s];
  if (exact) return exact;
  // Pattern fallback — ORDER MATTERS (composite labels).
  if (/wait|await|resolution|nulla osta|card collection|fingerprint|post.?approval|city hall|greffe|decision|on hold|submitted|filed\b/.test(s)) return 'waiting';
  // Re-submission and the consulate appointment are OUR work again → final;
  // a plain "submission (of documents)" means the file is out → waiting.
  if (/re-?submission|appoi?ntment/.test(s)) return 'final';
  if (/\bsubmission\b/.test(s)) return 'waiting';
  if (/file prep|drafting|final check|filing/.test(s)) return 'final';
  if (/previous formalit|list of documents|power of attorney|\bnif\b|bank account|intake|new case/.test(s)) return 'docs';
  if (/gather|review|translation|filiation|civil act|document|docs/.test(s)) return 'review';
  return 'docs';
}
